import { useState } from "react";
import { ItemList, Item } from "@/components/ui/item";
import { Stat, StatGrid } from "@/components/ui/stat";
import { Frame, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { useAuth, useUser } from "@clerk/react";
import NumberFlow from "@number-flow/react";
import {
  ChevronDown,
  CopyIcon,
  InfinityIcon,
  MailIcon,
  TriangleAlertIcon,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useDashboardStore } from "@/stores";
import type { UsageMetric } from "@/types/types";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Sidebar usage rows (Activated Creators / Campaigns) with the same dialog
 * treatment as Client Funds: the row is the trigger, the dialog carries the
 * explanation, capacity alerts, and stats grids (plus the campaign list on
 * the Campaigns one).
 */

const RESET_COPY: Record<string, string> = {
  billing_cycle: "Resets at the start of each billing cycle.",
  forever: "Lifetime count — it never resets.",
  per_organization: "Lifetime count for this organization — it never resets.",
};

/** Big limits are rendered as ∞ everywhere (same convention as the row). */
const isUnlimited = (limit: number) => limit >= 10000;

/** Sidebar usage row — the dialog trigger, same look as the funds row. */
function UsageDialogRow({
  label,
  current,
  limit,
}: {
  label: string;
  current: number;
  limit: number;
}) {
  return (
    <DialogTrigger className="flex flex-col w-full items-center gap-1 hover:bg-muted p-1.5 rounded-sm cursor-pointer">
      <div className="flex flex-row items-center justify-between text-xs w-full text-foreground/80">
        <p className="font-normal">{label}</p>
        <span className="flex flex-row items-center gap-0.5 font-mono font-medium">
          <span>{current}</span>
          <span>/</span>
          <span>
            {isUnlimited(limit) ? (
              <InfinityIcon className="size-3.5" strokeWidth={3} />
            ) : (
              limit
            )}
          </span>
        </span>
      </div>
      <Progress
        variant="segmented"
        value={limit > 0 ? Math.min((current / limit) * 100, 100) : 0}
        className="h-2"
        max={100}
      />
    </DialogTrigger>
  );
}

/** Staff-only row: a creator sitting on 2+ campaign_creators rows in the org. */
interface DuplicateCreatorRow {
  creator_id: string;
  email: string;
  count: number;
  username: string;
}

export function ActivatedCreatorsDialog({ usage }: { usage?: UsageMetric }) {
  const { getToken, orgId } = useAuth();
  const { user } = useUser();
  const {
    creatorStats: stats,
    creatorStatsLoading: loading,
    fetchCreatorStats,
  } = useDashboardStore();
  const [open, setOpen] = useState(false);

  // Staff (Clerk publicMetadata uplodio_member) also get the org-wide
  // duplicate-creator check on open; the server re-verifies the flag.
  const isStaff = Boolean(
    (user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member
  );
  const [dupes, setDupes] = useState<DuplicateCreatorRow[] | null>(null);
  const [dupesLoading, setDupesLoading] = useState(false);

  // Fresh fetch on every open — org-scoped, so org switches stay correct.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && orgId) {
      void (async () => {
        const token = await getToken();
        if (token) await fetchCreatorStats(orgId, token);
      })();
      if (isStaff) {
        setDupesLoading(true);
        void (async () => {
          try {
            const token = await getToken();
            const res = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/api/campaigns/getDuplicateOrgCreators`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ organization_id: orgId }),
              }
            );
            const json = await res.json();
            setDupes(json.success ? json.duplicates : []);
          } catch {
            setDupes([]);
          } finally {
            setDupesLoading(false);
          }
        })();
      }
    }
  };

  const current = usage?.current ?? 0;
  const limit = usage?.limit ?? 0;
  const unlimited = isUnlimited(limit);
  const pct = limit > 0 ? (current / limit) * 100 : 0;
  const atLimit = !unlimited && limit > 0 && current >= limit;
  const nearLimit = !atLimit && !unlimited && pct >= 90;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <ScopeWrapper className="w-full">
        <UsageDialogRow
          label="Activated Creators"
          current={current}
          limit={limit}
        />
      </ScopeWrapper>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Activated Creators</DialogTitle>
          <DialogDescription>
            A creator uses one slot the moment Amy closes the deal and they
            reach waiting-approval. Rejecting a creator doesn't return the
            slot. {RESET_COPY[usage?.reset_period ?? ""] ?? ""}
          </DialogDescription>
        </DialogHeader>

        {atLimit && (
          <Alert variant="destructive">
            <AlertTitle>
              <TriangleAlertIcon />
              At your creator limit
            </AlertTitle>
            <AlertDescription>
              Amy has stopped reaching out to new creators and only keeps
              replying to already-activated ones. Upgrade your plan to resume
              outreach.
            </AlertDescription>
          </Alert>
        )}
        {nearLimit && (
          <Alert variant="warning">
            <AlertTitle>
              <TriangleAlertIcon />
              Close to your creator limit
            </AlertTitle>
            <AlertDescription>
              Once the limit is reached, Amy stops contacting new creators
              until you upgrade or the count resets.
            </AlertDescription>
          </Alert>
        )}

        {/* The sidebar bar at dialog scale: used-of-limit. */}
        <div className="flex flex-col gap-1 mb-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Used</span>
            <span className="flex items-center gap-1 font-mono text-base font-medium">
              <NumberFlow value={current} />
              <span>/</span>
              {unlimited ? (
                <InfinityIcon className="size-4" strokeWidth={3} />
              ) : (
                <NumberFlow value={limit} />
              )}
            </span>
          </div>
          <Progress
            variant="segmented"
            value={limit > 0 ? Math.min((current / limit) * 100, 100) : 0}
            className="h-5"
            segments={80}
            max={100}
          />
        </div>

        {stats && (
          <div className="flex flex-col gap-1.5">
            {/* volume → engagement → outcomes, one row each */}
            <StatGrid className="animate-in fade-in">
              <Stat
                label="Total creators"
                value={stats.total}
                highlight
                tooltip="Every creator added across all your campaigns, including ones not contacted yet."
              />
              <Stat
                label="Queued"
                value={Math.max(stats.total - stats.contacted, 0)}
                tooltip="Added to a campaign but not contacted yet — Amy hasn't sent the first email."
              />
              <Stat
                label="Outreached"
                value={stats.contacted}
                tooltip="Creators Amy has emailed at least once."
              />
              <Stat
                label="Reply rate"
                value={
                  stats.contacted > 0 ? stats.replied / stats.contacted : 0
                }
                format={{ style: "percent", maximumFractionDigits: 1 }}
                tooltip="Creators who replied at least once, out of everyone outreached."
              />
              <Stat
                label="In talks"
                value={stats.in_talks}
                tooltip="Currently negotiating with Amy."
              />
              <Stat
                label="Waiting approval"
                value={stats.waiting}
                tooltip="Deals Amy closed that are waiting for your approve / reject."
              />
              <Stat
                label="Activated"
                value={stats.activated}
                tooltip="Closed deals — creators at waiting-approval or beyond (approved, in stages, completed)."
              />
              <Stat
                label="Activation rate"
                value={stats.replied > 0 ? stats.activated / stats.replied : 0}
                format={{ style: "percent", maximumFractionDigits: 1 }}
                tooltip="Activated creators out of everyone who replied."
              />
              <Stat
                label="Declined"
                value={stats.declined}
                tooltip="Creators who declined the partnership or were rejected by you."
              />
            </StatGrid>
          </div>
        )}

        {loading && <Skeleton className="h-46.5 w-full rounded-lg animate-out fade-out" />}

        {/* Staff-only: same duplicate check ops runs by hand — creators on
            2+ campaign_creators rows across the org (the duplicate-outreach
            incident class), styled like the sending-inboxes list. */}
        {isStaff && (
          <Collapsible className="flex flex-col gap-2">
            <CollapsibleTrigger className="group flex cursor-pointer items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              <ChevronDown className="size-3 transition-transform group-data-[state=open]:rotate-180" />
              Duplicated creators across campaigns · staff only
              {!dupesLoading && dupes && dupes.length > 0 && (
                <span className="font-mono font-medium tabular-nums">
                  · {dupes.length}
                </span>
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-2">
            {!dupesLoading && dupes && dupes.length === 0 && (
              <FramePanel className="p-3 text-xs text-muted-foreground animate-in fade-in">
                No creator appears in more than one campaign.
              </FramePanel>
            )}
            {!dupesLoading && dupes && dupes.length > 0 && (
              <ItemList className="max-h-56 animate-in fade-in">
                  {dupes.map((d) => (
                    <Item key={d.creator_id} variant="row" size="sm" className="flex-nowrap gap-2 text-xs">
                      <CopyIcon className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-medium truncate">
                        {d.username ? `@${d.username}` : d.email || d.creator_id}
                      </span>
                      {d.username && d.email && (
                        <span className="text-muted-foreground truncate">
                          {d.email}
                        </span>
                      )}
                      <span className="ml-auto shrink-0 font-mono font-medium tabular-nums">
                        {d.count}×
                      </span>
                    </Item>
                  ))}
              </ItemList>
            )}
            {dupesLoading && (
              <Skeleton className="h-24 w-full rounded-lg animate-out fade-out" />
            )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CampaignsUsageDialog({ usage }: { usage?: UsageMetric }) {
  const { getToken, orgId } = useAuth();
  const {
    inboxStats,
    creatorStatsLoading: loading,
    fetchCreatorStats,
  } = useDashboardStore();
  const [open, setOpen] = useState(false);

  // Fresh fetch on every open — org-scoped, so org switches stay correct.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && orgId) {
      void (async () => {
        const token = await getToken();
        if (token) await fetchCreatorStats(orgId, token);
      })();
    }
  };

  const current = usage?.current ?? 0;
  const limit = usage?.limit ?? 0;
  const unlimited = isUnlimited(limit);
  const atLimit = !unlimited && limit > 0 && current >= limit;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <ScopeWrapper className="w-full">
        <UsageDialogRow label="Campaigns" current={current} limit={limit} />
      </ScopeWrapper>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Campaigns</DialogTitle>
          <DialogDescription>
            Campaigns created on your current plan. Onboarding campaigns don't
            count toward the limit. {RESET_COPY[usage?.reset_period ?? ""] ?? ""}
          </DialogDescription>
        </DialogHeader>

        {atLimit && (
          <Alert variant="destructive">
            <AlertTitle>
              <TriangleAlertIcon />
              Campaign limit reached
            </AlertTitle>
            <AlertDescription>
              You've used every campaign in your plan — upgrade to create more.
            </AlertDescription>
          </Alert>
        )}

        <StatGrid>
          <Stat label="Used" value={current} highlight />
          <Stat label="Limit" value={unlimited ? "unlimited" : limit} />
          <Stat
            label="Remaining"
            value={unlimited ? "unlimited" : Math.max(limit - current, 0)}
          />
        </StatGrid>

        {/* Per sending inbox: how each mailbox is performing. */}
        <Frame>
          <FrameHeader>
            <FrameTitle>Sending inboxes</FrameTitle>
          </FrameHeader>
          {inboxStats.length > 0 && (
            <ItemList className="max-h-72 min-h-72 animate-in fade-in">
                {inboxStats.map((inbox) => (
                  <Item key={inbox.email} variant="row" size="sm" className="flex-nowrap gap-2 text-xs">
                    <MailIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="font-medium truncate">{inbox.email}</span>
                    <span className="text-muted-foreground shrink-0">
                      · {inbox.campaigns} campaign
                      {inbox.campaigns === 1 ? "" : "s"}
                    </span>
                    <span className="ml-auto flex shrink-0 items-center gap-3 text-muted-foreground">
                      <span>
                        <span className="text-foreground font-medium tabular-nums">
                          {inbox.contacted}
                        </span>{" "}
                        contacted
                      </span>
                      <span>
                        <span className="text-foreground font-medium tabular-nums">
                          {inbox.contacted > 0
                            ? `${((inbox.replied / inbox.contacted) * 100).toFixed(1)}%`
                            : "—"}
                        </span>{" "}
                        replies
                      </span>
                      <span>
                        <span className="text-foreground font-medium tabular-nums">
                          {inbox.activated}
                        </span>{" "}
                        activated
                      </span>
                    </span>
                  </Item>
                ))}
            </ItemList>
          )}
          {loading && <Skeleton className="h-72 w-full rounded-lg animate-out fade-out" />}
        </Frame>

      </DialogContent>
    </Dialog>
  );
}
